// Utilidades compartidas de inventario de equipos: clasificacion visual,
// generacion del acta de entrega y exportacion del inventario.
// Antes estaban duplicadas (y ya divergidas) en Clientes.js, TecnicoPage.js
// y RecursosHumanosPage.js; se centralizan aqui para que haya un solo
// comportamiento sin importar por donde se llegue al equipo.
import { useState } from 'react';
import { SUTRAK_LOGO, isSutrak, htmlEscape, imprimirHTML } from './acta';
import { construirHtmlInventario } from './inventarioHtml';

export const fmtMoney = v => new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', minimumFractionDigits: 0 }).format(v || 0);


export function copiarTexto(texto) {
  const t = String(texto || '');
  if (navigator.clipboard && navigator.clipboard.writeText) return navigator.clipboard.writeText(t).catch(() => copiarFallback(t));
  copiarFallback(t);
  return Promise.resolve();
}
function copiarFallback(t) {
  const ta = document.createElement('textarea');
  ta.value = t; ta.style.position = 'fixed'; ta.style.opacity = '0';
  document.body.appendChild(ta); ta.select();
  try { document.execCommand('copy'); } catch (e) { /* sin portapapeles */ }
  document.body.removeChild(ta);
}
const MONO = "'Consolas','Courier New',monospace";

// Linea compacta: chulito (validada) + caja + licencia completa con boton de copiar.
export function LicenciaOfficeLinea({ equipo }) {
  const [copiado, setCopiado] = useState(false);
  const lic = equipo?.office_licencia;
  if (!lic) return <span style={{ color: '#9ab0bf' }}>{equipo?.office_serial ? `...${equipo.office_serial}` : '-'}</span>;
  const copiar = e => { e.stopPropagation(); copiarTexto(lic).then(() => { setCopiado(true); setTimeout(() => setCopiado(false), 1400); }); };
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
      {Number(equipo.office_validada) === 1 && <span title="Licencia validada" style={{ color: '#059669', fontWeight: 900 }}>{'✓'}</span>}
      {equipo.office_caja && <span title="Numero de caja" style={{ fontSize: 10, fontWeight: 800, background: '#eef2f6', border: '1px solid #d8e5ec', borderRadius: 3, padding: '1px 5px' }}>Caja {equipo.office_caja}</span>}
      <span style={{ fontFamily: MONO, fontSize: 11, userSelect: 'all' }}>{lic}</span>
      <button type="button" onClick={copiar} style={{ fontSize: 10, fontWeight: 800, border: '1px solid #d8e5ec', background: copiado ? '#e6f7ed' : '#fff', color: copiado ? '#167247' : '#274762', borderRadius: 3, padding: '2px 6px', cursor: 'pointer', fontFamily: 'inherit' }}>{copiado ? 'Copiado' : 'Copiar'}</button>
    </span>
  );
}

// Bloque de la ficha: ver, copiar y editar licencia completa, caja y validacion.
export function LicenciaOfficeEditor({ equipo, onGuardar }) {
  const [editando, setEditando] = useState(false);
  const [form, setForm] = useState({});
  const [msg, setMsg] = useState('');
  const abrir = () => { setForm({ office_licencia: equipo.office_licencia || '', office_caja: equipo.office_caja || '', office_validada: Number(equipo.office_validada) === 1 }); setMsg(''); setEditando(true); };
  const guardar = async e => {
    e.preventDefault();
    try { await onGuardar({ ...form, office_licencia: String(form.office_licencia || '').trim().toUpperCase() }); setEditando(false); setMsg('Guardado'); }
    catch (err) { setMsg(err?.response?.data?.error || 'No se pudo guardar'); }
  };
  const lbl = { fontSize: 9, fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#7f9aad' };
  return (
    <div className="fe-fila" style={{ alignItems: 'flex-start' }}>
      <small>Licencia Office</small>
      <span style={{ display: 'grid', gap: 6, minWidth: 0 }}>
        {!editando && <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <LicenciaOfficeLinea equipo={equipo} />
          <button type="button" onClick={abrir} style={{ fontSize: 10, fontWeight: 800, border: '1px solid #d8e5ec', background: '#fff', borderRadius: 3, padding: '2px 8px', cursor: 'pointer', fontFamily: 'inherit' }}>Editar</button>
          {msg && <span style={{ fontSize: 11, fontWeight: 700, color: msg === 'Guardado' ? '#059669' : '#dc2626' }}>{msg}</span>}
        </span>}
        {editando && <form onSubmit={guardar} style={{ display: 'grid', gap: 8, maxWidth: 420 }}>
          <label style={{ display: 'grid', gap: 3 }}><span style={lbl}>Licencia completa (XXXXX-XXXXX-XXXXX-XXXXX-XXXXX)</span>
            <input value={form.office_licencia} onChange={e => setForm(f => ({ ...f, office_licencia: e.target.value }))} style={{ fontFamily: MONO, fontSize: 12, padding: 6, border: '1px solid #d8e5ec' }} /></label>
          <label style={{ display: 'grid', gap: 3 }}><span style={lbl}>Numero de caja</span>
            <input value={form.office_caja} onChange={e => setForm(f => ({ ...f, office_caja: e.target.value }))} style={{ fontSize: 12, padding: 6, border: '1px solid #d8e5ec', maxWidth: 120 }} /></label>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12 }}>
            <input type="checkbox" checked={!!form.office_validada} onChange={e => setForm(f => ({ ...f, office_validada: e.target.checked }))} /> Licencia validada {'✓'}</label>
          <span style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <button type="submit" style={{ fontSize: 11, fontWeight: 800, border: 'none', background: '#00b8e3', color: '#fff', borderRadius: 3, padding: '5px 12px', cursor: 'pointer', fontFamily: 'inherit' }}>Guardar</button>
            <button type="button" onClick={() => setEditando(false)} style={{ fontSize: 11, border: '1px solid #d8e5ec', background: '#fff', borderRadius: 3, padding: '5px 10px', cursor: 'pointer', fontFamily: 'inherit' }}>Cancelar</button>
            {msg && <span style={{ fontSize: 11, fontWeight: 700, color: '#dc2626' }}>{msg}</span>}
          </span>
        </form>}
      </span>
    </div>
  );
}


// Correo asociado al equipo: ver, copiar y editar (ficha).
export function CorreoEquipoEditor({ equipo, onGuardar }) {
  const [editando, setEditando] = useState(false);
  const [valor, setValor] = useState('');
  const [copiado, setCopiado] = useState(false);
  const [msg, setMsg] = useState('');
  const correo = equipo?.correo_electronico;
  const guardar = async e => {
    e.preventDefault();
    try { await onGuardar(valor.trim()); setEditando(false); setMsg('Guardado'); }
    catch (err) { setMsg(err?.response?.data?.error || 'No se pudo guardar'); }
  };
  const btn = { fontSize: 10, fontWeight: 800, border: '1px solid #d8e5ec', background: '#fff', borderRadius: 3, padding: '2px 8px', cursor: 'pointer', fontFamily: 'inherit' };
  return (
    <div className="fe-fila" style={{ alignItems: 'flex-start' }}>
      <small>Correo electrónico</small>
      <span style={{ display: 'grid', gap: 6, minWidth: 0 }}>
        {!editando && <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          {correo ? <a href={`mailto:${correo}`} style={{ color: '#0b6fa4', wordBreak: 'break-all' }}>{correo}</a> : <span style={{ color: '#9ab0bf' }}>{'\u2014'}</span>}
          {correo && <button type="button" onClick={() => copiarTexto(correo).then(() => { setCopiado(true); setTimeout(() => setCopiado(false), 1400); })} style={{ ...btn, background: copiado ? '#e6f7ed' : '#fff', color: copiado ? '#167247' : '#274762' }}>{copiado ? 'Copiado' : 'Copiar'}</button>}
          <button type="button" onClick={() => { setValor(correo || ''); setMsg(''); setEditando(true); }} style={btn}>Editar</button>
          {msg && <span style={{ fontSize: 11, fontWeight: 700, color: msg === 'Guardado' ? '#059669' : '#dc2626' }}>{msg}</span>}
        </span>}
        {editando && <form onSubmit={guardar} style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <input type="email" value={valor} onChange={e => setValor(e.target.value)} placeholder="usuario@empresa.com" style={{ fontSize: 12, padding: 6, border: '1px solid #d8e5ec', minWidth: 240 }} />
          <button type="submit" style={{ fontSize: 11, fontWeight: 800, border: 'none', background: '#00b8e3', color: '#fff', borderRadius: 3, padding: '5px 12px', cursor: 'pointer', fontFamily: 'inherit' }}>Guardar</button>
          <button type="button" onClick={() => setEditando(false)} style={{ ...btn, padding: '5px 10px' }}>Cancelar</button>
          {msg && <span style={{ fontSize: 11, fontWeight: 700, color: '#dc2626' }}>{msg}</span>}
        </form>}
      </span>
    </div>
  );
}

export const COLOR_EQUIPO = {
  verde: { bg: '#e6f7ed', border: '#86d3a5', color: '#167247', label: 'Mantenimiento al día' },
  magenta: { bg: '#fde8f4', border: '#eca6cf', color: '#a32973', label: 'Crítico para cambio' },
  pendiente: { bg: '#dbeafe', border: '#64b5f6', color: '#1e5aa8', label: 'Pendiente' },
  baja: { bg: '#e5e7eb', border: '#9ca3af', color: '#4b5563', label: 'Dado de baja' },
};

export const colorEquipoStyle = eq => {
  const c = COLOR_EQUIPO[eq?.clasificacion_color];
  return c ? { background: c.bg, borderColor: c.border } : {};
};

export function parsarDiscos(raw) {
  if (!raw) return '—';
  try {
    const arr = JSON.parse(raw);
    if (Array.isArray(arr)) {
      return arr.map(d => {
        const modelo = d.Modelo || d.modelo || d.model || d.Model || '';
        const tam    = d.Tamaño || d.tamaño || d.size || d.Size || d.Capacidad || '';
        return [modelo, tam].filter(Boolean).join(' — ');
      }).filter(Boolean).join(' | ') || '—';
    }
    return String(arr);
  } catch { return String(raw); }
}

// Selector del "estado visual" del equipo (verde / magenta / sin color).
export function SelectorColorEquipo({ equipo, onChange, compacto = false }) {
  const [abierto, setAbierto] = useState(false);
  const valor = equipo?.clasificacion_color || '';
  const c = COLOR_EQUIPO[valor];
  return (
    <>
      <button type="button" onClick={e => { e.stopPropagation(); setAbierto(true); }}
        title={c?.label || 'Sin categorizar'}
        style={{ width: compacto ? '100%' : 'auto', fontSize: 11, fontWeight: 800, color: c?.color || '#fff',
          background: c?.bg || '#00b8e3', border: `1px solid ${c?.border || '#00b8e3'}`,
          borderRadius: 4, padding: '5px 8px', cursor: 'pointer', fontFamily: 'inherit' }}>
        Estado
      </button>
      {abierto && (
        <div onClick={e => { e.stopPropagation(); setAbierto(false); }}
          style={{ position: 'fixed', inset: 0, zIndex: 2100, background: 'rgba(8,41,63,0.32)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
          <div onClick={e => e.stopPropagation()} style={{ width: '100%', maxWidth: 360, background: '#fff', border: '1px solid #d8e5ec', borderRadius: 6, padding: 18, boxShadow: '0 18px 45px rgba(8,41,63,0.22)' }}>
            <div style={{ fontSize: 11, fontWeight: 900, color: '#5a7a8a', letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 5 }}>Estado del equipo</div>
            <div style={{ fontSize: 15, fontWeight: 900, color: '#08293f', marginBottom: 14 }}>{equipo?.nombre_equipo || 'Equipo'}</div>
            {[
              ['', 'Sin color', 'Sin categorizar', '#fff', '#d8e5ec', '#5a7a8a'],
              ['verde', 'Verde', 'Mantenimiento al día', COLOR_EQUIPO.verde.bg, COLOR_EQUIPO.verde.border, COLOR_EQUIPO.verde.color],
              ['magenta', 'Magenta claro', 'Estado crítico para cambio', COLOR_EQUIPO.magenta.bg, COLOR_EQUIPO.magenta.border, COLOR_EQUIPO.magenta.color],
              ['pendiente', 'Azul', 'Pendiente (queda en una seccion aparte)', COLOR_EQUIPO.pendiente.bg, COLOR_EQUIPO.pendiente.border, COLOR_EQUIPO.pendiente.color],
              ['baja', 'Gris', 'Dado de baja (se oculta de la lista principal)', COLOR_EQUIPO.baja.bg, COLOR_EQUIPO.baja.border, COLOR_EQUIPO.baja.color],
            ].map(([key, label, detail, bg, border, color]) => (
              <button key={key || 'sin-color'} type="button" onClick={() => { onChange(key); setAbierto(false); }}
                style={{ width: '100%', textAlign: 'left', display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'center',
                  background: bg, border: `1px solid ${border}`, borderRadius: 4, padding: '10px 12px', marginBottom: 8, cursor: 'pointer', fontFamily: 'inherit' }}>
                <span><b style={{ display: 'block', color }}>{label}</b><small style={{ color: '#5a7a8a' }}>{detail}</small></span>
                {valor === key && <span style={{ color, fontWeight: 900 }}>✓</span>}
              </button>
            ))}
            <button type="button" onClick={() => setAbierto(false)} className="btn-secondary" style={{ width: '100%', marginTop: 4 }}>Cancelar</button>
          </div>
        </div>
      )}
    </>
  );
}

// Diseño unico del acta de entrega (antes "Diseño A" de Clientes.js).
// `equipo.observaciones_entrega`, si existe, se imprime en el bloque de
// Observaciones (antes solo lo hacia la copia de RecursosHumanosPage.js).
export function generarActaEntrega({ empresa, infoEmpresa = {}, equipo }) {
  const fecha = new Date().toLocaleDateString('es-CO', { day: 'numeric', month: 'long', year: 'numeric' });
  const responsable = equipo.responsable || '________________________________________';
  const nit = infoEmpresa.nit || '________________________________';
  const valor = equipo.valor_comercial ? fmtMoney(equipo.valor_comercial) : '________________________________';
  const sutrak = isSutrak(empresa);
  const brand = sutrak ? `<img class="logo" src="${SUTRAK_LOGO}" alt="SUTRAK" />` : '<div class="brand">AGE Informatica &middot; Inventario</div>';
  const campos = [
    ['Equipo', equipo.nombre_equipo || equipo.identidad_valor || '-'],
    ['Área', equipo.area || '-'],
    ['Usuario', equipo.usuario_windows || '-'],
    ['Serial', equipo.serial || '-'],
    ['Sistema operativo', [equipo.windows, equipo.windows_version].filter(Boolean).join(' - ') || '-'],
    ['Procesador', equipo.procesador || '-'],
    ['Memoria RAM', equipo.memoria_ram_gb ? `${equipo.memoria_ram_gb} GB` : '-'],
    ['Discos', parsarDiscos(equipo.discos)],
    ['MAC', equipo.mac_wifi || equipo.mac_ethernet || '-'],
    ['Valor comercial', valor],
  ];
  const observaciones = String(equipo.observaciones_entrega || '').trim();
  const observacionesHtml = observaciones ? htmlEscape(observaciones).replace(/\n/g, '<br>') : '';
  const html = `<!doctype html><html><head><title>Acta de entrega - ${htmlEscape(equipo.nombre_equipo || empresa)}</title><style>
    @page{size:A4;margin:18mm}*{box-sizing:border-box}body{font-family:Arial,sans-serif;color:#08293f;margin:0;font-size:12px;line-height:1.5}
    .head{border-bottom:3px solid #00b8e3;padding-bottom:12px;margin-bottom:22px}.brand{font-size:11px;letter-spacing:.16em;text-transform:uppercase;color:#5a7a8a;font-weight:700}.logo{max-width:245px;max-height:76px;object-fit:contain}
    h1{font-size:23px;margin:5px 0 0}.meta{color:#5a7a8a;margin-top:3px}.company{border:1px solid #d8e5ec;background:#f7fafc;padding:12px 14px;margin-bottom:18px}
    .label{font-size:9px;letter-spacing:.1em;text-transform:uppercase;color:#7f9aad;font-weight:700}.value{font-size:13px;font-weight:700}
    p{margin:0 0 16px;text-align:justify}.grid{display:grid;grid-template-columns:1fr 1fr;border-top:1px solid #d8e5ec;border-left:1px solid #d8e5ec;margin:14px 0 20px}
    .cell{min-height:54px;padding:8px 10px;border-right:1px solid #d8e5ec;border-bottom:1px solid #d8e5ec}.full{grid-column:1/-1}
    .obs{height:76px;border:1px solid #d8e5ec;margin-top:5px}.signatures{display:grid;grid-template-columns:1fr 1fr;gap:50px;margin-top:58px}
    .signature{border-top:1px solid #08293f;padding-top:7px}.muted{color:#5a7a8a;font-size:10px}@media print{button{display:none}}
  </style></head><body>
    <div class="head">${brand}<h1>Acta de entrega de equipo</h1><div class="meta">Fecha de entrega: ${htmlEscape(fecha)}</div></div>
    <div class="company"><div class="label">Empresa</div><div class="value">${htmlEscape(empresa)}</div><div style="margin-top:7px"><span class="label">NIT / CC: </span><b>${htmlEscape(nit)}</b></div>${infoEmpresa.direccion ? `<div><span class="label">Dirección: </span>${htmlEscape(infoEmpresa.direccion)}</div>` : ''}</div>
    <p>Por medio de la presente se deja constancia de que <b>${htmlEscape(empresa)}</b>, identificada con NIT/CC <b>${htmlEscape(nit)}</b>, asigna y entrega al responsable <b>${htmlEscape(responsable)}</b> el equipo descrito a continuación para el desarrollo de sus funciones.</p>
    <div class="grid">${campos.map(([label, value]) => `<div class="cell"><div class="label">${htmlEscape(label)}</div><div class="value">${htmlEscape(value)}</div></div>`).join('')}</div>
    <p>Quien recibe declara haber verificado el equipo y recibirlo en condiciones de funcionamiento, comprometiéndose a darle un uso adecuado, conservarlo y reportar oportunamente cualquier novedad, daño, pérdida o cambio de asignación.</p>
    <div class="label">Observaciones</div><div class="obs">${observacionesHtml}</div>
    <div class="signatures">
      <div class="signature"><b>Entrega</b><br><span class="muted">Nombre: ______________________________<br>Cargo: _______________________________<br>Documento: ___________________________</span></div>
      <div class="signature"><b>Recibe: ${htmlEscape(responsable)}</b><br><span class="muted">Documento: ___________________________<br>Firma: _______________________________<br>Fecha: _______________________________</span></div>
    </div>
    <script>window.onload=()=>setTimeout(()=>window.print(),250)</script>
  </body></html>`;
  imprimirHTML(html);
}

export function exportarInventarioExcel({ nombre, titulo, equipos }) {
  const rows = equipos || [];
  const cols = [
    ['Empresa / cliente', eq => eq.empresa || eq.cliente || nombre],
    ['Area', eq => eq.area || 'Sin area'],
    ['Equipo', eq => eq.nombre_equipo || eq.identidad_valor || ''],
    ['Responsable', eq => eq.responsable || ''],
    ['Usuario Windows', eq => eq.usuario_windows || ''],
    ['Serial', eq => eq.serial || ''],
    ['Sistema operativo', eq => [eq.windows, eq.windows_version].filter(Boolean).join(' - ')],
    ['Procesador', eq => eq.procesador || ''],
    ['RAM GB', eq => eq.memoria_ram_gb || ''],
    ['Tarjeta de video', eq => eq.tarjeta_video || ''],
    ['Discos', eq => parsarDiscos(eq.discos) === '—' ? '' : parsarDiscos(eq.discos)],
    ['MAC WiFi', eq => eq.mac_wifi || ''],
    ['MAC Ethernet', eq => eq.mac_ethernet || ''],
    ['Correo', eq => eq.correo_electronico || ''],
    ['Office', eq => [eq.office_version, eq.office_licencia || eq.office_serial].filter(Boolean).join(' - ')],
    ['Caja Office', eq => eq.office_caja || ''],
    ['Licencia validada', eq => Number(eq.office_validada) === 1 ? 'Si' : ''],
    ['Ultimo mantenimiento', eq => eq.ultimo_mantenimiento ? String(eq.ultimo_mantenimiento).slice(0, 10) : ''],
    ['Valor comercial', eq => eq.valor_comercial || ''],
    ['Estado visual', eq => eq.clasificacion_color === 'verde' ? 'Mantenimiento al dia' : eq.clasificacion_color === 'magenta' ? 'Critico para cambio' : eq.clasificacion_color === 'baja' ? 'Dado de baja' : eq.clasificacion_color === 'pendiente' ? 'Pendiente' : 'Sin categorizar'],
  ];
  const html = `<!doctype html><html><head><meta charset="UTF-8"></head><body><table border="1"><thead><tr>${cols.map(([h]) => `<th>${htmlEscape(h)}</th>`).join('')}</tr></thead><tbody>${rows.map(eq => `<tr>${cols.map(([, get]) => `<td>${htmlEscape(get(eq))}</td>`).join('')}</tr>`).join('')}</tbody></table></body></html>`;
  const blob = new Blob(['﻿' + html], { type: 'application/vnd.ms-excel;charset=utf-8;' });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  const safe = String(titulo || nombre || 'inventario').replace(/[^a-z0-9_-]+/gi, '-').replace(/^-+|-+$/g, '').toLowerCase() || 'inventario';
  a.href = url;
  a.download = `${safe}.xls`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
}

// Impresion del inventario (listado, fichas o ambos). El HTML lo arma lib/inventarioHtml.js.
// modo: 'completo' | 'resumen' | 'fichas'; area: nombre del area si se imprime una; infoEmpresa:
// datos de la empresa (nit, direccion, correo) para el encabezado de la ficha de un solo equipo.
export function imprimirInventarioEmpresa({ empresa, titulo, equipos, modo = 'completo', area = '', infoEmpresa = null }) {
  imprimirHTML(construirHtmlInventario({ empresa, titulo, equipos, modo, area, infoEmpresa }));
}
